import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Input, Button, Tooltip, Tabs } from 'antd';

import { useTranslation } from 'react-i18next';

import Table from '@components/Table';
import useSwr from '@hooks/useSwr';

import useSearchParams from '@hooks/useSearchParams';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useCheckRole } from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import SelectCustomers from '@components/SelectCustomers';
import CustomTag from '@components/CustomTag';
import { useNavigate } from 'react-router-dom';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import { bytesFormat } from '@utils/formatters';
import NewGroupingModal, { GroupingPayload } from './NewGroupingModal';
import AdminActionsGrouping from './AdminActionsGrouping';
import * as S from './styles';

const { Search } = Input;
const { TabPane } = Tabs;

interface GroupingsData {
  content: GroupingPayload[];
  totalElements?: number;
}

interface GroupingsProps {
  onlyTable?: boolean;
  sharingPlanId?: number;
}

const Groupings = ({ onlyTable, sharingPlanId }: GroupingsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['groupings', 'attributes', 'requests']);
  const canManage = useCheckRole({ role: rolesConstants.MANAGE_GROUPINGS });

  const [nameValue, setNameValue] = useState<string>();
  const [openModal, setOpenModal] = useState<boolean>(false);

  const toggleOpen = useCallback(() => setOpenModal(crr => !crr), []);

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    defaultParams: ['listingType'],
    extraParams: ['name', 'departmentId'],
  });

  const { listingType, name, departmentId, page, linesPerPage } = useMemo(
    () => searchParams,
    [searchParams],
  );

  const { data, mutate } = useSwr<GroupingsData>(
    `/service-proxy/broker/inventory-groups${
      listingType === 'archived' ? '/archived' : ''
    }`,
    {
      params: {
        page: page ? Number(page) - 1 : 0,
        linesPerPage: linesPerPage ?? 20,
        name,
        departmentId,
        sharingPlanId,
      },
    },
  );

  useEffect(() => {
    setNameValue(name);
  }, [name]);

  const navigateToSharedPlan = useCallback(
    (sharedPlanId: number) => {
      navigate(
        `/${ApplicationsConstants.MOT_BUSINESS}/mobile-network/administrative/shared-plans/${sharedPlanId}`,
      );
    },
    [navigate],
  );

  const handleMutate = useCallback(
    (type: 'new' | 'archiving', groupingToBeChanged: GroupingPayload) => {
      if (data) {
        if (type === 'new') {
          mutate({
            ...data,
            content: [groupingToBeChanged, ...data.content].slice(0, 10),
          });
        } else {
          mutate({
            ...data,
            content: [
              ...data.content.filter(
                grouping => grouping.id !== groupingToBeChanged?.id,
              ),
            ],
          });
        }
      }
    },
    [data, mutate],
  );

  const columns = useMemo(() => {
    const allColumns = [
      {
        title: t('attributes:id'),
        dataIndex: 'id',
      },
      {
        title: t('attributes:description'),
        dataIndex: 'name',
      },
      {
        title: t('attributes:department'),
        dataIndex: 'department',
        render: (department: { name: string }) => department?.name,
      },
      {
        title: t('sharingType.title'),
        dataIndex: 'sharingType',
        align: 'center' as const,
        render: (sharingType: number) => t(`sharingType.${sharingType}`),
      },
      {
        title: t('attributes:sharedPlan'),
        dataIndex: 'sharingPlan',
        align: 'center' as const,
        render: (sharingPlan: { id: number; description: string }) =>
          sharingPlan && (
            <Tooltip title={t('msgs.viewSharedPlan')}>
              <Button
                type="link"
                onClick={() => navigateToSharedPlan(sharingPlan?.id)}
              >
                {sharingPlan?.description}
              </Button>
            </Tooltip>
          ),
      },
      {
        title: t('attributes:franchise'),
        dataIndex: 'franchiseBytes',
        align: 'center' as const,
        render: bytesFormat,
      },
      {
        title: t('attributes:automaticBlocking.title'),
        dataIndex: 'automaticBlocking',
        align: 'center' as const,
        render: (value: string) => (
          <CustomTag color={value ? 'green' : 'red'}>
            {t(
              `attributes:automaticBlocking.${
                value ? 'activated' : 'disabled'
              }`,
            ).toUpperCase()}
          </CustomTag>
        ),
      },
      {
        title: '',
        dataIndex: 'actions',
        align: 'center' as const,
        render: (_: any, record: GroupingPayload) => (
          <AdminActionsGrouping
            mutate={() => {
              handleMutate('archiving', { id: record?.id } as GroupingPayload);
            }}
            showArchived={listingType !== 'archived'}
            item={record}
          />
        ),
      },
    ];

    return allColumns?.filter(
      col =>
        !(['sharingType', 'sharingPlan'].includes(col.dataIndex) && onlyTable),
    );
  }, [handleMutate, listingType, navigateToSharedPlan, onlyTable, t]);

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        label: t('attributes:description'),
        element: (
          <Search
            onSearch={value => setSearchParams({ name: value })}
            onChange={({ target: { value } }) => setNameValue(value)}
            value={nameValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('attributes:department'),
        element: (
          <SelectCustomers
            value={departmentId && Number(departmentId)}
            onChange={value => setSearchParams({ departmentId: value })}
            isMotBusiness
          />
        ),
      },
    ],
    [t, setSearchParams, nameValue, departmentId],
  );

  return (
    <S.GroupingsContainer>
      {!onlyTable && (
        <NewGroupingModal
          open={openModal}
          onClose={toggleOpen}
          mutateData={newGrouping => handleMutate('new', newGrouping)}
        />
      )}

      <Tabs
        type="card"
        defaultActiveKey="active"
        activeKey={listingType}
        onChange={value => setSearchParams({ listingType: value })}
      >
        {['active', 'archived'].map(item => (
          <TabPane key={item} tab={t(`visions.${item}`)} />
        ))}
      </Tabs>
      <Table
        title={t('groupings')}
        columns={columns}
        data={data?.content ?? []}
        loading={!data?.content}
        tableKey="groupings"
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        extraActions={
          canManage &&
          !onlyTable && (
            <Tooltip placement="top" title={t('newGrouping')}>
              <Button icon={<PlusCircleOutlined />} onClick={toggleOpen} />
            </Tooltip>
          )
        }
        onTableChange={setSearchParams}
        pagination={{ page, linesPerPage, total: data?.totalElements }}
      />
    </S.GroupingsContainer>
  );
};

export default Groupings;
